import { useCallback, useEffect, useMemo } from 'react';
import { gql, useMutation } from '@apollo/client';
import { useLocation } from '@gatsbyjs/reach-router';
import {
  ascFormSubmissionSales,
  ascFormSubmissionSalesAppt,
  getGaSessionId,
} from 'helpers/src/gtag';
import mapApolloData from 'gatsby-theme-core/src/helpers/map-apollo-data';
import sbjs from 'sourcebuster';

// abmulder uses most of these fields for proefrit
const returnAttributes = `
  ${process.env.GATSBY_STRAPI_VERSION === '5' ? 'documentId' : ''}
  name
  street
  housenumber
  housenumberExt
  postalcode
  city
  country
  dateOfBirth
  tel
  email
  company
  acceptNewsletter
  leadType
  subject
  message
  url
  fields # json
`;

const useCreateSubmission = (
  formValues,
  name,
  {
    formId,
    vehicleId,
    ctaText,
    updateLead,
    createAttributes,
    updateAttributes,
  } = {},
) => {
  const CREATE_SUBMISSION = useMemo(() => gql`
    mutation($data: FormsSubmissionInput!) {
      createFormsSubmission(data: $data) {
        data {
          id
          attributes {
            ${returnAttributes}
            ${createAttributes || ''}
          }
        }
      }
    }
  `, [createAttributes]);

  const UPDATE_SUBMISSION = useMemo(() => gql`
    mutation($id: ID!, $data: FormsSubmissionInput!) {
      updateFormsSubmission(${process.env.GATSBY_STRAPI_VERSION === '5' ? 'documentId' : 'id'}: $id, data: $data) {
        data {
          id
          attributes {
            ${returnAttributes}
            ${updateAttributes || ''}
          }
        }
      }
    }
  `, [updateAttributes]);

  const gaSessionId = useMemo(() => getGaSessionId(), []);
  const { state, origin, href } = useLocation();
  const {
    // allow transitioning to this page with an exisiting lead
    lead: stateLead,
  } = state || {};
  // this component allows rendering with a created lead, adjusting its values
  // it can come as a prop, or it can come via a page transition in the history state
  const exisitingLead = updateLead || stateLead;

  const [
    mutate,
    {
      data,
      loading,
      error,
    },
  ] = useMutation(exisitingLead ? UPDATE_SUBMISSION : CREATE_SUBMISSION);

  const {
    fields,
    ...variables
  } = formValues;

  const {
    toCompany,
    toCompanyDepartment,
    ...restFields
  } = fields;

  const onSubmit = useCallback(async (e) => {
    e.preventDefault();
    try {
      await mutate({
        variables: {
          id: exisitingLead ? (exisitingLead.documentId || exisitingLead.id) : undefined,
          data: {
            ...(formId ? { form: formId } : {}),
            ...(vehicleId ? {
              vehicle: vehicleId,
            } : {}),
            source: origin,
            url: href,
            ...variables,
            ...(toCompany ? { toCompany } : {}),
            ...(toCompanyDepartment ? { toCompanyDepartment } : {}),
            fields: {
              ...(restFields || {}),
              ...(gaSessionId ? { gaSessionId } : {}),
              ...(sbjs?.get ? {
                ...sbjs.get.current,
                ...sbjs.get.current_add,
                first_cmp: sbjs.get.first.cmp,
                first_cnt: sbjs.get.first.cnt,
                first_mdm: sbjs.get.first.mdm,
                first_src: sbjs.get.first.src,
                first_trm: sbjs.get.first.trm,
                first_typ: sbjs.get.first.typ,
                first_ep: sbjs.get.first_add.ep,
                first_fd: sbjs.get.first_add.fd,
                first_rf: sbjs.get.first_add.rf,
                ...sbjs.get.session,
                vst: sbjs.get.udata.vst,
                uag: sbjs.get.udata.uag,
              } : {}),
            },
          },
        },
      });
    } catch (err) {
      if (typeof Sentry === 'undefined') {
        console.warn('There is no Sentry, not sending...');
        console.error(err);
        return;
      }
      Sentry.captureException(err, (scope) => {
        scope.clear();
        scope.setContext('form', { name, href });
        scope.setContext('input', formValues);
        scope.setContext('exisitingLead', exisitingLead);
        return scope;
      });
    }
  }, [
    mutate,
    exisitingLead,
    formId,
    vehicleId,
    gaSessionId,
    formValues,
    restFields,
    toCompany,
    toCompanyDepartment,
    variables,
    href,
    origin,
    name,
  ]);

  const selectedLeadType = formValues.leadType;
  useEffect(() => {
    if (data) {
      const lead = mapApolloData((data.createFormsSubmission || data.updateFormsSubmission).data);
      const isAppt = ['proefrit', 'afspraak'].includes(selectedLeadType?.toLowerCase());
      const fn = isAppt ? ascFormSubmissionSalesAppt : ascFormSubmissionSales;
      let comm_outcome = 'crm_update';
      // we assume updates are always specific deals
      if (isAppt) {
        comm_outcome = 'send_appt';
      }
      fn({
        // event_owner,
        // page_type,
        comm_type: 'form',
        comm_outcome,
        submission_id: lead?.id,
        element_text: ctaText,
        department: 'sales',
        form_name: name,
        form_type: vehicleId ? 'item' : 'consumer_contact',
        ...(vehicleId ? { vehicle: lead.vehicle } : {}),
      });
    }
  }, [
    data,
    name,
    ctaText,
    selectedLeadType,
    vehicleId,
  ]);

  return {
    onSubmit,
    data,
    lead: mapApolloData((data?.createFormsSubmission || data?.updateFormsSubmission)?.data),
    loading,
    error,
  };
};

export default useCreateSubmission;
